/* eslint-disable max-lines-per-function */
import { useCallback, useState } from 'react';

import {
   ButtonVariant,
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalMaxHeight,
   ModalWidth,
   StyledFlex,
} from '@dreryk/react-components';

import { appointmentsTranslationKeys } from '../../../translations';
import { usePreInterviewGlobalState } from '../../table/PreInterviewResolver.hooks';
import { PreInterviewApproveAction } from '../shared/actions/PreInterviewApproveAction';
import { SurveyUpdateAction } from '../shared/actions/SurveyUpdateAction';
import { PreInterviewFormLoader } from '../shared/form/PreInterviewForm.loader';
import { PatientInformationView } from '../shared/PatientInformationView/PatientInformationView';

import { PreInterviewCreateForm } from './PreInterviewCreateForm';
import { PreInterviewInitializeAction } from './PreInterviewCreateModal.actions';
import { preInterviewModalConfirmExitConfig } from './PreInterviewCreateModal.utils';

type PreInterviewCreateModalProps = {
   readonly appointmentId: number;
   readonly beginDate: Date;
   readonly endDate: Date;
   readonly onClose: VoidFunction;
   readonly patientFullName: string;
};

// eslint-disable-next-line max-lines-per-function
export const PreInterviewCreateModal = (props: PreInterviewCreateModalProps): JSX.Element => {
   const [surveyId, setSurveyId] = useState<number | null>(null);
   const { updateSinglePreInterviewState } = usePreInterviewGlobalState();

   const handleSurveyUpdateSuccess = useCallback(() => {
      props.onClose();
      dispatchSnackbarPositiveEvent(
         appointmentsTranslationKeys.preInterview.modal.create.form.actions.save.successMessage
      );
      updateSinglePreInterviewState(props.appointmentId, { surveyId: surveyId!, status: 'STARTED' });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.appointmentId, surveyId, props.onClose, updateSinglePreInterviewState]);

   const handlePreInterviewApproveSuccess = useCallback(() => {
      props.onClose();
      updateSinglePreInterviewState(props.appointmentId, { surveyId: surveyId!, status: 'APPROVED' });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.appointmentId, surveyId, props.onClose, updateSinglePreInterviewState]);

   const handlePreInterviewExpiredFault = useCallback(() => {
      updateSinglePreInterviewState(props.appointmentId, null);
   }, [props.appointmentId, updateSinglePreInterviewState]);

   const handleSurveyNotUpdatableFault = useCallback(() => {
      dispatchSnackbarNegativeEvent(
         appointmentsTranslationKeys.preInterview.modal.create.form.errors.surveyNotUpdatableFault
      );
   }, []);

   return (
      <Modal
         testId={'preInterviewCreate'}
         title={appointmentsTranslationKeys.preInterview.modal.shared.title}
         width={ModalWidth.WIDE}
         height={ModalMaxHeight.TALL}
      >
         <StyledFlex direction={'column'} gap={24}>
            <PatientInformationView
               patientFullName={props.patientFullName}
               beginDate={props.beginDate}
               endDate={props.endDate}
            />

            <PreInterviewFormLoader>
               {({ preInterviewTemplate }) => (
                  <PreInterviewCreateForm preInterviewTemplate={preInterviewTemplate}>
                     {({ handleSubmit, formState }) => (
                        <>
                           <SurveyUpdateAction
                              onSuccess={handleSurveyUpdateSuccess}
                              onSurveyNotUpdatableFault={handleSurveyNotUpdatableFault}
                           >
                              {({ updateSurvey }) => (
                                 <PreInterviewInitializeAction
                                    appointmentId={props.appointmentId}
                                    surveyTemplateVersionId={preInterviewTemplate.templateVersionId}
                                    onSurveyInitialized={setSurveyId}
                                    onPreInterviewExpiredFault={handlePreInterviewExpiredFault}
                                 >
                                    {({ initializePreInterview }) => (
                                       <ModalConfirmButton
                                          onClick={handleSubmit(async (answers) => {
                                             const { surveyId } = await initializePreInterview(answers);
                                             await updateSurvey({
                                                surveyId: surveyId,
                                                answers: answers,
                                             });
                                          })}
                                          testId={'save'}
                                          label={
                                             appointmentsTranslationKeys.preInterview.modal.create.form.actions.save
                                                .title
                                          }
                                          variant={ButtonVariant.SECONDARY}
                                       />
                                    )}
                                 </PreInterviewInitializeAction>
                              )}
                           </SurveyUpdateAction>
                           <PreInterviewApproveAction
                              appointmentId={props.appointmentId}
                              onSuccess={handlePreInterviewApproveSuccess}
                              onPreInterviewExpiredFault={handlePreInterviewExpiredFault}
                           >
                              {({ approvePreInterview }) => (
                                 <SurveyUpdateAction
                                    onSuccess={approvePreInterview}
                                    onSurveyNotUpdatableFault={handleSurveyNotUpdatableFault}
                                 >
                                    {({ updateSurvey }) => (
                                       <PreInterviewInitializeAction
                                          appointmentId={props.appointmentId}
                                          surveyTemplateVersionId={preInterviewTemplate.templateVersionId}
                                          onSurveyInitialized={setSurveyId}
                                          onPreInterviewExpiredFault={handlePreInterviewExpiredFault}
                                       >
                                          {({ initializePreInterview }) => (
                                             <ModalConfirmButton
                                                onClick={handleSubmit(async (answers) => {
                                                   const { surveyId } = await initializePreInterview(answers);
                                                   await updateSurvey({
                                                      surveyId: surveyId,
                                                      answers: answers,
                                                   });
                                                })}
                                                testId={'confirm'}
                                                label={
                                                   appointmentsTranslationKeys.preInterview.modal.create.form.actions
                                                      .confirm.title
                                                }
                                                variant={ButtonVariant.PRIMARY}
                                             />
                                          )}
                                       </PreInterviewInitializeAction>
                                    )}
                                 </SurveyUpdateAction>
                              )}
                           </PreInterviewApproveAction>
                           <ModalCancelButton
                              onClick={props.onClose}
                              shouldConfirmExit={preInterviewModalConfirmExitConfig(formState.isDirty)}
                           />
                           <ModalCloseButton
                              onClick={props.onClose}
                              shouldConfirmExit={preInterviewModalConfirmExitConfig(formState.isDirty)}
                           />
                        </>
                     )}
                  </PreInterviewCreateForm>
               )}
            </PreInterviewFormLoader>
         </StyledFlex>
      </Modal>
   );
};
